import { action, computed, observable } from 'mobx'
import type { WorkspaceSearcher } from '@/features/iam/workspaces/WorkspaceSearcher'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { type BundleStore } from './BundleStore'

export class WorkspaceSearchViewModel implements WorkspaceSearcher {
  private readonly _searchText = new SearchTextViewModel()

  /**
   * User is searching a workspace
   */
  @observable accessor editing = false

  constructor(private readonly _bundleStore: BundleStore) {}

  /**
   * Search text computed getter.
   */
  @computed
  get searchText() {
    return this._searchText.value
  }

  /**
   * Returns only active workspaces
   */
  @computed
  get activeWorkspaces() {
    return (
      this._bundleStore.workspacesWithBundles
        // Workspaces show once they have loaded.
        .filter((item) => item.workspace.name !== '')
        .sort((a, b) => a.workspace.name.localeCompare(b.workspace.name))
    )
  }

  /**
   * Filtered workspaces
   */
  @computed
  get filteredItems() {
    const result = this.activeWorkspaces

    if (this.searchText) {
      const searchText = this.searchText.toLowerCase()

      return result.filter((item) =>
        item.workspace.name.toLocaleLowerCase().includes(searchText),
      )
    }
    return result
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  /**
   * Sets the search text.
   *
   * @param searchText
   */
  @action.bound
  setSearchText(searchText: string) {
    this._searchText.change(searchText)
  }

  async refreshWorkspacesWithBundles(signal?: AbortSignal) {
    await this._bundleStore.fetchWorkspacesWithBundles(signal)
  }
}
